import React, { useState } from 'react';
import { Wrench, Package, Clock, CheckCircle2, AlertCircle, Eye, X } from 'lucide-react';

/**
 * StructuredUpdateCard Component
 * Displays inline repair progress updates in chat when a message carries structuredData.
 * Scoped to fields: status (badge), note (text), photos (thumbnails), estimatedCompletion.
 */
const StructuredUpdateCard = ({ data = {}, message = {}, isSender = false }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Extract fields with fallbacks
  const status = (data.status || 'in_progress').toLowerCase();
  const noteText = data.note || message.content?.text || message.contentText || '';
  const photos = Array.isArray(data.photos) ? data.photos : (Array.isArray(data.images) ? data.images : []);
  const estimatedCompletion = data.estimatedCompletion || data.estimatedDelivery || null;

  const getStatusConfig = () => {
    switch (status) {
      case 'parts_ordered':
        return {
          label: 'Parts Ordered',
          badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
          icon: <Package className="w-4 h-4 text-amber-600" />,
        };
      case 'in_repair':
      case 'in_progress':
        return {
          label: 'Repair In Progress',
          badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
          icon: <Wrench className="w-4 h-4 text-blue-600" />,
        };
      case 'awaiting_pickup':
        return {
          label: 'Awaiting Pickup',
          badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
          icon: <Clock className="w-4 h-4 text-purple-600" />,
        };
      case 'completed':
      case 'done':
        return {
          label: 'Repair Completed',
          badgeClass: 'bg-green-100 text-green-800 border-green-200',
          icon: <CheckCircle2 className="w-4 h-4 text-green-600" />,
        };
      default:
        return {
          label: status.replace(/_/g, ' '),
          badgeClass: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: <AlertCircle className="w-4 h-4 text-gray-600" />,
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className="w-80 md:w-96 rounded-2xl shadow-sm border border-gray-200 overflow-hidden bg-white">
      {/* Header with Progress Badge */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-gray-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-600">
            Repair Progress Update
          </span>
        </div>
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 border ${statusConfig.badgeClass}`}
        >
          {statusConfig.icon}
          {statusConfig.label}
        </span>
      </div>

      {/* Body: Note & Details */}
      <div className="p-4 space-y-3">
        {noteText && (
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <p className="text-sm text-gray-800 leading-relaxed break-words whitespace-pre-wrap">
              {noteText}
            </p>
          </div>
        )}

        {/* Estimated Completion if available */}
        {estimatedCompletion && (
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-blue-50/50 px-3 py-2 rounded-lg border border-blue-100/50">
            <Clock className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
            <span>
              Est. Completion: <strong className="text-gray-700">{estimatedCompletion}</strong>
            </span>
          </div>
        )}

        {/* Photo Thumbnails */}
        {photos.length > 0 && (
          <div>
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
              Photos ({photos.length})
            </span>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((photoUrl, idx) => (
                <div
                  key={idx}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100 cursor-pointer"
                  onClick={() => setSelectedImage(photoUrl)}
                >
                  <img
                    src={photoUrl}
                    alt={`Repair update photo ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder-image.png';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Eye className="w-5 h-5 text-white" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-2xl max-h-[90vh] bg-transparent"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-10 right-0 p-1.5 text-white hover:text-gray-300 rounded-full bg-black/50"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={selectedImage}
              alt="Expanded preview"
              className="max-h-[85vh] max-w-full rounded-xl object-contain shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StructuredUpdateCard;
